import { fetchMyStats } from '@/api/profile';
import ClientsChart from '@/components/stats/clients-chart';
import PeriodFilter from '@/components/stats/period-filter';
import ProfitsChart from '@/components/stats/profits-chart';
import SummaryCards from '@/components/stats/summary-cards';
import TopBuildsChart from '@/components/stats/top-builds-chart';
import type { StatsPeriod } from '@/entities/admin-stats.entity';
import type { IWorkerStats } from '@/entities/worker-stats.entity';
import { formatProfitUsd } from '@/lib/format-profit';
import { BoxIcon, DollarSignIcon, UsersIcon, WifiIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import ProfitHeroCard from './profit-hero-card';
import ProfileStatsSkeleton, { ProfileStatsDataSkeleton } from './profile-stats-skeleton';
import RankBadges from './rank-badges';
import RecentProfits from './recent-profits';

export default function ProfileStatsSection() {
	const [period, setPeriod] = useState<StatsPeriod>('week');
	const [stats, setStats] = useState<IWorkerStats | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		fetchMyStats(period)
			.then(data => {
				if (!cancelled) setStats(data);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [period]);

	function handlePeriodChange(next: StatsPeriod) {
		if (next === period) return;
		setPeriod(next);
		setLoading(true);
	}

	if (loading && !stats) {
		return <ProfileStatsSkeleton />;
	}

	if (!stats) {
		return <ProfileStatsSkeleton />;
	}

	return (
		<div className='flex flex-col gap-4'>
			<ProfitHeroCard profitAmount={stats.allTime.profitAmount} profitCount={stats.allTime.profitCount} />

			<PeriodFilter period={period} onChange={handlePeriodChange} />

			{loading ? (
				<ProfileStatsDataSkeleton />
			) : (
				<>
					<SummaryCards
						cards={[
							{ icon: UsersIcon, label: 'Клиенты', value: stats.totals.clients.toLocaleString('ru-RU'), color: 'text-chart-2' },
							{ icon: WifiIcon, label: 'Онлайн', value: stats.totals.onlineClients.toLocaleString('ru-RU'), color: 'text-chart-2' },
							{ icon: DollarSignIcon, label: 'Профит', value: `$${formatProfitUsd(stats.totals.profits)}`, color: 'text-chart-5' },
							{ icon: BoxIcon, label: 'Билды', value: stats.totals.builds.toLocaleString('ru-RU'), color: 'text-foreground' },
						]}
					/>

					<p className='text-xs text-muted-foreground text-center'>
						Всего: {stats.allTime.clientsCount} клиентов · {stats.allTime.buildsCount} успешных билдов
					</p>

					<RankBadges byClients={stats.rank.byClients} byProfit={stats.rank.byProfit} />

					<ClientsChart data={stats.clientsOverTime} />
					<ProfitsChart data={stats.profitsOverTime} />
					<TopBuildsChart data={stats.topBuilds} title='Мои билды' showWorker={false} />
					<RecentProfits profits={stats.recentProfits} />
				</>
			)}
		</div>
	);
}
